import React, { useState, useEffect, useCallback } from "react";
import { Route } from "react-router-dom";
import { CSSTransition } from "react-transition-group";

import Loading from "./components/loading";
import Navbar from "./components/navbar";
import Background from "./components/background";
import ParticleNetwork from "./components/ParticleNetwork";
import Home from "./components/pages/home";
import About from "./components/pages/about";
import Portfolio from "./components/pages/portfolio";
import Contact from "./components/pages/contact";
import Footer from "./components/footer";
import ChatWidget from "./components/ChatWidget";
import ScrollProgress from "./components/ScrollProgress";
import CustomCursor from "./components/CustomCursor";
import CommandPalette from "./components/CommandPalette";
import Toast from "./components/Toast";
import SmoothScroll from "./components/SmoothScroll";
import useScrollReveal from "./components/useScrollReveal";
import "./App.scss";

export const routes = [
  { path: "/", name: "Home", Component: Home },
  { path: "/about", name: "About", Component: About },
  { path: "/portfolio", name: "Portfolio", Component: Portfolio },
  { path: "/contact", name: "Contact", Component: Contact },
];

function App() {
  useScrollReveal();
  const [loading, setLoading] = useState(() => {
    if (typeof window !== "undefined") {
      if (
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ) {
        return false;
      }
      if (sessionStorage.getItem("portfolio_intro_seen") === "true") {
        return false;
      }
    }
    return true;
  });

  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const triggerToast = useCallback((msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(""), 3000);
  }, []);

  useEffect(() => {
    // Recruiter console greeting
    console.log(
      "%c Welcome, Engineer / Recruiter! %c\nI am Pratyush Kumar Rai — Full-Stack Developer & AI Builder.\nLet's build something remarkable together!\nEmail: pratyushkumarrai3@gmail.com | GitHub: github.com/PratyushKumarRai",
      "background: #0ea5e9; color: #0f172a; font-weight: bold; font-size: 14px; padding: 6px 12px; border-radius: 6px;",
      "color: #cbd5e1; font-size: 12px; line-height: 1.8;"
    );

    // Global Command Palette Shortcut: Ctrl+K / Cmd+K
    const handleGlobalKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setIsPaletteOpen((prev) => !prev);
      }
    };

    // Konami Code Easter Egg
    const konamiSequence = [
      "ArrowUp", "ArrowUp", "ArrowDown", "ArrowDown",
      "ArrowLeft", "ArrowRight", "ArrowLeft", "ArrowRight",
      "b", "a"
    ];
    let konamiIndex = 0;

    const handleKonami = (e) => {
      if (e.key.toLowerCase() === konamiSequence[konamiIndex].toLowerCase()) {
        konamiIndex++;
        if (konamiIndex === konamiSequence.length) {
          triggerToast("🎮 Konami Code Unlocked! You found the secret easter egg ✨");
          konamiIndex = 0;
        }
      } else {
        konamiIndex = 0;
      }
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    window.addEventListener("keydown", handleKonami);

    return () => {
      window.removeEventListener("keydown", handleGlobalKeyDown);
      window.removeEventListener("keydown", handleKonami);
    };
  }, [triggerToast]);

  return (
    <div className="App">
      <SmoothScroll isPaletteOpen={isPaletteOpen} />
      <ScrollProgress />
      <CustomCursor />
      <Navbar onOpenPalette={() => setIsPaletteOpen(true)} />
      <Background />
      <ParticleNetwork introFinished={!loading} />

      {/* 3D Emergence Wrapper for Smooth Fly-Through Transition */}
      <div id="site-3d-container" className={loading ? "is-3d" : ""}>
        <div id="site-3d-wrapper">
          <div className="main-container">
            {routes.map(({ path, Component }) => (
              <Route key={path} exact path={path}>
                {({ match }) => (
                  <CSSTransition
                    in={match != null}
                    timeout={350}
                    classNames="page"
                    unmountOnExit
                  >
                    <div className="page">
                      <Component
                        onToast={triggerToast}
                        introFinished={!loading}
                      />
                      {Component !== Home ? <Footer /> : null}
                    </div>
                  </CSSTransition>
                )}
              </Route>
            ))}
          </div>
        </div>
      </div>

      <CommandPalette
        isOpen={isPaletteOpen}
        onClose={() => setIsPaletteOpen(false)}
        onToast={triggerToast}
      />

      <Toast message={toastMessage} />

      {/* Digital Twin chatbot — visible across all pages */}
      <ChatWidget />

      {/* Iconic "P" Monogram Intro with 3D Fly-Through Doorway */}
      {loading && <Loading onFinish={() => setLoading(false)} />}
    </div>
  );
}

export default App;
